import React from 'react';
import { useTranslation } from 'react-i18next';
import manifest from '../../seo/shotManifest.json';
import styles from './blog.module.css';

const SUPPORTED = ['en', 'he', 'ar'];

/** Flat lookup, because a post names a shot by slug and not by feature. */
const BY_SLUG = Object.values(manifest).flat().reduce((acc, s) => {
  acc[s.slug] = s;
  return acc;
}, {});

export const shotBySlug = (slug) => BY_SLUG[slug] || null;

/** Intrinsic size of the built WebP — same table ShotStrip uses, so the
 *  space is reserved and the image cannot shift the text under it. */
const DIMS = { d: { width: 1200, height: 750 }, p: { width: 420, height: 860 } };

export const shotAlt = (slug, lang) => {
  const shot = BY_SLUG[slug];
  if (!shot) return '';
  const l = SUPPORTED.includes(lang) ? lang : 'en';
  return (shot.alt && (shot.alt[l] || shot.alt.en)) || '';
};

/**
 * A REAL SCREENSHOT INSIDE A POST, in the reader's own language.
 *
 * The owner asked for pictures from the system and the site already had 29 of
 * them per language, built by scripts/buildShots.js from the operator-guide
 * captures and used on the feature pages. Reusing those rather than inventing
 * art means the picture in an Arabic post is the Arabic portal, and nothing new
 * has to be kept in step.
 *
 * The caption is the manifest's alt text, so the words are translated once and
 * a post's body carries only the slug — which is also why the three languages
 * keep an identical block shape, as everyPostIsReadableInEveryLanguage demands.
 *
 * `loading="lazy"` with explicit width/height: a post's shots sit well below
 * the fold, so they cost a reader nothing until scrolled to, and they cannot
 * move the paragraph they interrupt. An unknown slug renders NOTHING rather
 * than a broken frame.
 */
export default function BlogShot({ slug }) {
  const { i18n } = useTranslation();
  const shot = BY_SLUG[slug];
  if (!shot) return null;

  const base = (i18n.language || 'en').split('-')[0];
  const lang = SUPPORTED.includes(base) ? base : 'en';
  const dims = DIMS[shot.device] || DIMS.d;
  const words = shotAlt(slug, lang);

  return (
    <figure className={`${styles.shot} ${shot.device === 'p' ? styles.shotPhone : ''}`}>
      <img
        src={`/shots/${lang}/${shot.slug}.webp`}
        alt={words}
        width={dims.width}
        height={dims.height}
        loading="lazy"
        decoding="async"
      />
      <figcaption>{words}</figcaption>
    </figure>
  );
}
